<template>
    <div>
        <span class="text-lg">
            Trạng thái phân bổ của đơn hàng
        </span>
        <div class="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-x-4 gap-y-8">
            <analytics-card
                :text="$t('labels.allocationCompleted')"
                :value="$formatters.formatCurrency(dataAnalytics.allocationCompleted)"
                color="success"
                increase
                @click="getItems('allocationCompleted')" />
            <analytics-card
                :text="$t('labels.inadequateAllocation')"
                :value="$formatters.formatCurrency(dataAnalytics.unloadingAllocation)"
                color="accent"
                increase
                @click="getItems('inadequateAllocation')" />
            <analytics-card
                :text="$t('labels.waitingForAllocation')"
                :value="$formatters.formatCurrency(dataAnalytics.waitingForAllocation)"
                color="accent"
                increase
                @click="getItems('waitingForAllocation')" />
            <analytics-card
                :text="$t('labels.missingInputInformations')"
                :value="$formatters.formatCurrency(dataAnalytics.missingInputInformations)"
                color="error"
                increase
                @click="getItems('missingInputInformations')" />
        </div>
    </div>

    <analytics-modal
        v-model:visible="visible"
        :headers="headers"
        :loading="loading"
        :items="items" />
</template>
<script lang="ts" setup>
import AnalyticsCard from '../analytics-card.vue'
import AnalyticsModal from '../analytics-modal.vue'

import { $api, $lodash } from '#imports'
import Status from '~/components/status.vue'

const { $formatters } = useNuxtApp()

const dataAnalytics = ref<{
  allocationCompleted: number
  unloadingAllocation: number
  waitingForAllocation: number
  missingInputInformations: number
}>({
  allocationCompleted: 0,
  unloadingAllocation: 0,
  waitingForAllocation: 0,
  missingInputInformations: 0
})

onMounted(async () => {
  const api = $url.dashboard.directionOfFleet.getOrderAllocationStatus
  const data: any = await $api(api)
  const resData: any = $lodash.get(data, 'data.value')
  if (resData?.success) {
    dataAnalytics.value = resData.data
  }
})

const items = ref<Array<any>>([])
const store = $store()

const loading = ref(false)
const visible = ref(false)

const getItems = async (type: string) => {
  try {
    loading.value = true
    visible.value = true
    const api = $url.dashboard.directionOfFleet.getDataOrderAllocationStatus
    const data: any = await $api(api, { uriParams: { TYPE: type } })
    const resData: any = $lodash.get(data, 'data.value')
    if (resData?.success) {
      items.value = resData.data
    }
    loading.value = false
  } catch (e: any) {
    loading.value = false
    visible.value = false
  }
}

const handleViewDetail = (order: any) => {
  const routeData = useRouter().resolve({ name: 'trucker-id-order', query: { action: 'view', orderId: order._id } })
  window.open(routeData.href, '_blank')
}

const headers = ref([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      return params.data.code || ''
    }
  },
  {
    field: 'noBooking',
    headerName: $t('labels.noBooking'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      return params.data.noBooking || ''
    }
  },

  {
    field: 'quote',
    headerName: $t('labels.type'),
    sortable: true,
    valueGetter: (params: any) => {
      const quote = params.data?.quote
      if (quote) {
        return $t('labels.haveQuote')
      }

      return $t('labels.notHaveQuote')
    }
  },
  {
    field: 'productivityOrder',
    sortable: true,
    headerName: $t('labels.productivity'),
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.productivityOrder)
    }
  },

  {
    field: 'workingTimeRequest',
    sortable: true,
    headerName: $t('labels.workingTimeRequest'),
    valueGetter: (params: any) => {
      let value = ''
      const workingTimeRequest = params.data.workingTimeRequest
      if (workingTimeRequest.includes('overTime')) {
        value = $t('labels.nodeModule.overTime')
      }

      if (workingTimeRequest.includes('weekend')) {
        value = !value ? $t('labels.nodeModule.weekend') : value + ', ' + $t('labels.nodeModule.weekend')
      }

      if (workingTimeRequest.includes('holiday')) {
        value = !value ? $t('labels.holiday') : value + ', ' + $t('labels.holiday')
      }

      return value
    }
  },

  {
    field: 'transportation',
    sortable: true,
    headerName: $t('labels.transportation'),
    valueGetter: (params: any) => {
      const transportation = params.data.transportation
      if (transportation === 'CONTAINER') {
        return $t('labels.containerTruck')
      }
      if (transportation === 'NON_CONTAINER') {
        return $t('labels.nonContainerTruck')
      }
    }
  },
  {
    field: 'quoteCode',
    headerName: $t('labels.quote'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      const quoteCode = params.data?.quoteCode
      return quoteCode || ''
    }
  },
  {
    field: 'quote.chargeName',
    headerName: $t('labels.route'),
    valueGetter: (params: any) => {
      return params.data?.chargeName || ''
    }
  },
  {
    field: 'customerName',
    headerName: $t('labels.customer'),
    valueGetter: (params: any) => {
      return params.data.customerName || ''
    }
  },
  {
    field: 'contactName',
    headerName: $t('labels.contact'),
    valueGetter: (params: any) => {
      return params.data.contactName || ''
    }
  },
  {
    field: 'contactEmail',
    headerName: $t('labels.email'),
    valueGetter: (params: any) => {
      return params.data.contactEmail || ''
    }
  },
  {
    field: 'contactPhone',
    headerName: $t('labels.phone'),
    valueGetter: (params: any) => {
      return params.data.contactPhone || ''
    }
  },
  {
    field: 'businessOpportunityName',
    headerName: $t('labels.businessOpportunity'),
    valueGetter: (params: any) => {
      return params.data.businessOpportunityName || ''
    }
  },
  {
    field: 'commodityName',
    headerName: $t('labels.commodity'),
    valueGetter: (params: any) => {
      return params.data.commodityName || ''
    }
  },
  {
    field: 'workflowName',
    headerName: $t('labels.workflow'),
    valueGetter: (params: any) => {
      const createOrderFrom = params.data?.createOrderFrom
      if (createOrderFrom !== 'CUSTOMER_REQUEST') {
        return ''
      }
      return params.data.workflowName
    }
  },
  {
    field: 'mergeContainer',
    headerName: $t('labels.mergeContainer'),
    valueGetter: (params: any) => {
      if (params?.data?.mergeContainer) {
        return $t('labels.haveMergeContainer')
      }
      return $t('labels.notMergeContainer')
    }
  },
  {
    field: 'contCategory',
    headerName: $t('labels.cont'),
    valueGetter: (params: any) => {
      if (params.data?.transportation === 'CONTAINER' && params.data?.contCategory) {
        if (params.data?.contCategory === 20) {
          return $t('labels.20f')
        }
        if (params.data?.contCategory === 40) {
          return $t('labels.40f')
        }
        if (params.data?.contCategory === 45) {
          return $t('labels.45f')
        }
      }
    }
  },
  {
    field: 'vehicleCategoryName',
    headerName: $t('labels.vehicleType'),
    valueGetter: (params: any) => {
      return params.data.vehicleCategoryName || ''
    }
  },
  {
    field: 'headContainer',
    headerName: $t('labels.headContainer'),
    valueGetter: (params: any) => {
      if (params?.data?.headContainer) {
        return $t('labels.true')
      }
      return $t('labels.false')
    }
  },
  {
    field: 'productivity',
    headerName: $t('labels.productivityByMount'),
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.productivity)
    }
  },
  {
    field: 'weightCommodity',
    headerName: $t('labels.weightCommodityByKG1'),
    valueGetter: (params: any) => {
      const createOrderFrom = params.data?.createOrderFrom
      if (createOrderFrom !== 'CUSTOMER_REQUEST') {
        return ''
      }
      return $formatters.formatNumber(params.data.weightCommodity)
    }
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'orderStatus',
      label: '',
      keyStatus: 'status'
    }
  },
  {
    field: 'allocationStatus',
    headerName: $t('labels.allocationStatus'),
    cellRenderer: Status,
    cellRendererParams: {
      module: 'orderAllocationStatus',
      label: '',
      keyStatus: 'allocationStatus'
    }
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: 'DataTableActions',
    cellRendererParams: {
      storeKey: 'dashboardDataTable',
      storeName: 'Dashboard',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          click: handleViewDetail,
          isAll: true
        }
      ]
    }
  }
])
</script>
